import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {};

const slice = createSlice({
  name: 'supplierManagement',
  initialState,
  reducers: {},
});

export const api = createApi({
  reducerPath: 'supplierManagementApi',
  baseQuery,
  tagTypes: ['Suppliers'],
  endpoints: builder => ({
    // Get all suppliers with filtering and pagination
    getSuppliers: builder.query({
      query: params => ({
        url: endpoints.SUPPLIERS,
        method: 'GET',
        params,
      }),
      providesTags: ['Suppliers'],
    }),

    // Search suppliers
    searchSuppliers: builder.query({
      query: params => ({
        url: endpoints.SUPPLIERS_SEARCH,
        method: 'GET',
        params,
      }),
      providesTags: ['Suppliers'],
    }),

    // Get supplier by ID
    getSupplierById: builder.query({
      query: id => ({
        url: `${endpoints.SUPPLIERS}/${id}`,
        method: 'GET',
      }),
      providesTags: ['Suppliers'],
    }),

    // Create new supplier
    createSupplier: builder.mutation({
      query: data => ({
        url: endpoints.SUPPLIERS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Suppliers'],
    }),

    // Update supplier
    updateSupplier: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${endpoints.SUPPLIERS}/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Suppliers'],
    }),

    // Delete supplier
    deleteSupplier: builder.mutation({
      query: id => ({
        url: `${endpoints.SUPPLIERS}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Suppliers'],
    }),

    // Get supplier statistics
    getSupplierStats: builder.query({
      query: () => ({
        url: endpoints.SUPPLIERS_STATS,
        method: 'GET',
      }),
      providesTags: ['Suppliers'],
    }),

    // Get supplier reports
    getSupplierReports: builder.query({
      query: params => ({
        url: endpoints.SUPPLIERS_REPORTS,
        method: 'GET',
        params,
      }),
      providesTags: ['Suppliers'],
    }),
  }),
});

export const { actions: SupplierManagementActions } = slice;

export const useSupplierManagementSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: SupplierManagementActions,
    useGetSuppliersQuery: api.useLazyGetSuppliersQuery,
    useSearchSuppliersQuery: api.useLazySearchSuppliersQuery,
    useGetSupplierByIdQuery: api.useLazyGetSupplierByIdQuery,
    useCreateSupplierMutation: api.useCreateSupplierMutation,
    useUpdateSupplierMutation: api.useUpdateSupplierMutation,
    useDeleteSupplierMutation: api.useDeleteSupplierMutation,
    useGetSupplierStatsQuery: api.useLazyGetSupplierStatsQuery,
    useGetSupplierReportsQuery: api.useLazyGetSupplierReportsQuery,
  };
};
